import {
  startExperiment,
  StartExperimentInput,
  updateExperiment,
} from '@/services/DynamicPageVariantsService';
import * as Sentry from '@sentry/react';
import { Experiment, ExperimentMetricConfig } from '@superfunnel/types';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Button from '@superfunnel/ui-core/v2/Button';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import { doc, getDoc } from 'firebase/firestore';
import { Plus, Trash } from 'iconoir-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import { db } from '../Firebase';
import { emptyConverter } from '../utils';

// Default built-in metrics that can be tracked in experiments
const defaultMetrics: ExperimentMetricConfig[] = [
  { id: 'conversionRate', type: 'default', name: 'Conversion Rate' },
  { id: 'conversions', type: 'default', name: 'Conversions' },
  { id: 'buttonClicks', type: 'default', name: 'Button Clicks' },
  { id: 'formSubmissions', type: 'default', name: 'Form Submissions' },
];

// End modes for experiments
const endModes = [
  {
    id: 'manual',
    name: 'Manual',
    description: 'End the experiment manually when you decide',
  },
  {
    id: 'statisticalSignificance',
    name: 'Statistical Significance',
    description: 'End when a winner is determined with 95% confidence',
  },
  {
    id: 'duration',
    name: 'Fixed Duration',
    description: 'End after a specified number of days',
  },
  {
    id: 'visitors',
    name: 'Visitor Count',
    description: 'End after reaching a target number of visitors',
  },
] as const;

export interface ExperimentTestInput {
  id: string;
  description: string;
  trafficSplit: number;
  websiteId?: string; // Present for existing tests, undefined for new ones
}

export interface ExperimentFormData {
  name: string;
  metric: ExperimentMetricConfig;
  secondaryMetrics: ExperimentMetricConfig[];
  endMode: StartExperimentInput['endMode'];
  endModeValue?: number;
  minVisitorsPerVariant?: number;
  minDurationDays?: number;
  tests: ExperimentTestInput[];
}

/** @deprecated Use ExperimentFormData instead */
export type StartExperimentData = ExperimentFormData;

interface Props {
  /** The dynamic page ID for the experiment */
  dynamicPageId: string | undefined;
  /** The variant ID for the experiment */
  variantId: string | undefined;
  /** The variant name (used for default experiment name in create mode) */
  variantName: string | undefined;
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal is closed */
  onClose: () => void;
  /** Mode: 'create' for new experiments, 'edit' for updating existing */
  mode?: 'create' | 'edit';
  /** Experiment ID (required for edit mode) */
  experimentId?: string;
  /** Called when an experiment is successfully created or updated */
  onSuccess?: () => void;
}

export default function ExperimentModal({
  dynamicPageId,
  variantId,
  variantName,
  open,
  onClose,
  mode = 'create',
  experimentId,
  onSuccess,
}: Props) {
  const {
    selectedAccount,
    analyticsCustomEvents,
    analyticsExternalPixelEvents,
  } = useContext(UserContext);

  // Internal state for fetched experiment data (edit mode)
  const [existingExperiment, setExistingExperiment] = useState<Experiment>();
  const [fetchingExperiment, setFetchingExperiment] = useState(false);

  // Combine default metrics with custom and external events
  const availableMetrics = useMemo((): ExperimentMetricConfig[] => {
    const metrics: ExperimentMetricConfig[] = [...defaultMetrics];

    // Add external pixel events
    if (analyticsExternalPixelEvents) {
      for (const event of analyticsExternalPixelEvents) {
        metrics.push({
          id: event.id,
          type: 'externalPixel',
          name: event.name,
          description: event.description,
        });
      }
    }

    // Add custom analytics events
    if (analyticsCustomEvents) {
      for (const event of analyticsCustomEvents) {
        metrics.push({
          id: event.id,
          type: 'customEvent',
          name: event.name,
          description: event.description,
        });
      }
    }

    return metrics;
  }, [analyticsExternalPixelEvents, analyticsCustomEvents]);

  const [name, setName] = useState('');
  const [metricId, setMetricId] = useState('conversionRate');
  const [secondaryMetricIds, setSecondaryMetricIds] = useState<string[]>([]);
  const [endMode, setEndMode] = useState<StartExperimentInput['endMode']>(
    'statisticalSignificance',
  );
  const [endModeValue, setEndModeValue] = useState<number | undefined>(
    undefined,
  );
  const [minVisitorsPerVariant, setMinVisitorsPerVariant] = useState<
    number | undefined
  >(undefined);
  const [minDurationDays, setMinDurationDays] = useState<number | undefined>(
    undefined,
  );
  const [tests, setTests] = useState<ExperimentTestInput[]>([]);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);

  // Track original test IDs for edit mode (to detect added/removed tests)
  const [originalTestIds, setOriginalTestIds] = useState<Set<string>>(
    new Set(),
  );

  // Fetch experiment data when opening in edit mode
  useEffect(() => {
    if (
      open &&
      mode === 'edit' &&
      experimentId &&
      selectedAccount &&
      dynamicPageId
    ) {
      const fetchExperiment = async () => {
        setFetchingExperiment(true);
        try {
          const experimentRef = doc(
            db,
            'accounts',
            selectedAccount.id,
            'campaigns',
            dynamicPageId,
            'experiments',
            experimentId,
          ).withConverter(emptyConverter<Experiment>());
          const experimentDoc = await getDoc(experimentRef);
          if (experimentDoc.exists()) {
            setExistingExperiment(experimentDoc.data() as Experiment);
          }
        } catch (err) {
          console.error('Failed to fetch experiment:', err);
          Sentry.captureException(err);
          setError('Failed to load experiment data');
        } finally {
          setFetchingExperiment(false);
        }
      };
      fetchExperiment();
    } else if (!open) {
      // Clear experiment data when modal closes
      setExistingExperiment(undefined);
    }
  }, [open, mode, experimentId, selectedAccount, dynamicPageId]);

  // Populate form state when existing experiment is loaded (edit mode) or reset for create mode
  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && existingExperiment) {
      // Edit mode: populate with existing experiment data
      setName(existingExperiment.name);
      setMetricId(existingExperiment.metric.id);
      setSecondaryMetricIds(
        existingExperiment.secondaryMetrics.map((m) => m.id),
      );
      setEndMode(existingExperiment.endMode);
      setEndModeValue(existingExperiment.endModeValue);
      setMinVisitorsPerVariant(existingExperiment.minVisitorsPerVariant);
      setMinDurationDays(existingExperiment.minDurationDays);
      setTests(
        existingExperiment.tests.map((t) => ({
          id: t.id,
          description: t.description,
          trafficSplit: t.trafficSplit,
          websiteId: t.websiteId,
        })),
      );
      setOriginalTestIds(new Set(existingExperiment.tests.map((t) => t.id)));
      setError(undefined);
      setLoading(false);
    } else if (mode === 'create') {
      // Create mode: reset to defaults
      setName(variantName ? `${variantName} Experiment` : 'New Experiment');
      setMetricId('conversionRate');
      setSecondaryMetricIds([]);
      setEndMode('statisticalSignificance');
      setEndModeValue(undefined);
      setMinVisitorsPerVariant(undefined);
      setMinDurationDays(undefined);
      setTests([
        {
          id: crypto.randomUUID(),
          description: '',
          trafficSplit: 50,
        },
      ]);
      setOriginalTestIds(new Set());
      setError(undefined);
      setLoading(false);
    }
  }, [open, variantName, mode, existingExperiment]);

  // Available secondary metrics (excluding the primary metric)
  const availableSecondaryMetrics = useMemo(() => {
    return availableMetrics.filter((m) => m.id !== metricId);
  }, [availableMetrics, metricId]);

  // Get selected secondary metrics as full config objects
  const selectedSecondaryMetrics = useMemo(() => {
    return secondaryMetricIds
      .map((id) => availableMetrics.find((m) => m.id === id))
      .filter((m): m is ExperimentMetricConfig => m !== undefined);
  }, [secondaryMetricIds, availableMetrics]);

  // Handle secondary metrics change from ChipsAutocomplete
  const handleSecondaryMetricsChange = useCallback(
    (metrics: ExperimentMetricConfig[]) => {
      setSecondaryMetricIds(metrics.map((m) => m.id));
    },
    [],
  );

  // Clear secondary metrics if they include the new primary metric
  useEffect(() => {
    setSecondaryMetricIds((prev) => prev.filter((id) => id !== metricId));
  }, [metricId]);

  const totalTrafficSplit = useMemo(() => {
    // Original gets 100 - sum of test splits
    return tests.reduce((sum, test) => sum + test.trafficSplit, 0);
  }, [tests]);

  const originalTrafficSplit = 100 - totalTrafficSplit;

  const addTest = useCallback(() => {
    const newTestCount = tests.length + 1;
    const splitPerItem = Math.floor(100 / (newTestCount + 1)); // +1 for original
    const remainder = 100 - splitPerItem * (newTestCount + 1);

    setTests((prev) => {
      const updatedTests = prev.map((test) => ({
        ...test,
        trafficSplit: splitPerItem,
      }));
      return [
        ...updatedTests,
        {
          id: crypto.randomUUID(),
          description: '',
          trafficSplit: splitPerItem + remainder,
        },
      ];
    });
  }, [tests.length]);

  const removeTest = useCallback((id: string) => {
    setTests((prev) => {
      const filtered = prev.filter((test) => test.id !== id);
      if (filtered.length === 0) return filtered;

      // Redistribute traffic
      const splitPerItem = Math.floor(100 / (filtered.length + 1)); // +1 for original
      const remainder = 100 - splitPerItem * (filtered.length + 1);

      return filtered.map((test, index) => ({
        ...test,
        trafficSplit:
          index === filtered.length - 1
            ? splitPerItem + remainder
            : splitPerItem,
      }));
    });
  }, []);

  const updateTestDescription = useCallback(
    (id: string, description: string) => {
      setTests((prev) =>
        prev.map((test) => (test.id === id ? { ...test, description } : test)),
      );
    },
    [],
  );

  const updateTestTrafficSplit = useCallback(
    (id: string, trafficSplit: number) => {
      setTests((prev) =>
        prev.map((test) =>
          test.id === id
            ? {
                ...test,
                trafficSplit: Math.max(0, Math.min(100, trafficSplit)),
              }
            : test,
        ),
      );
    },
    [],
  );

  const isValidTrafficSplit = useMemo(() => {
    return originalTrafficSplit >= 0 && originalTrafficSplit <= 100;
  }, [originalTrafficSplit]);

  const canSubmit = useMemo(() => {
    if (!name.trim()) return false;
    if (tests.length === 0) return false;
    if (!isValidTrafficSplit) return false;
    if (tests.some((test) => !test.description.trim())) return false;
    if (
      (endMode === 'duration' || endMode === 'visitors') &&
      (!endModeValue || endModeValue <= 0)
    ) {
      return false;
    }
    return true;
  }, [name, tests, isValidTrafficSplit, endMode, endModeValue]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      setError('Please fill in all required fields');
      return;
    }

    if (!selectedAccount || !dynamicPageId || !variantId) {
      setError('Missing account, dynamic page, or variant information');
      return;
    }

    // Find the full metric config from ID
    const selectedMetric = availableMetrics.find((m) => m.id === metricId);
    if (!selectedMetric) {
      setError('Selected metric not found');
      return;
    }

    // Convert secondary metric IDs to full configs
    const selectedSecondaryMetricsData = secondaryMetricIds
      .map((id) => availableMetrics.find((m) => m.id === id))
      .filter((m): m is ExperimentMetricConfig => m !== undefined);

    setLoading(true);
    setError(undefined);

    try {
      if (mode === 'edit' && experimentId && existingExperiment) {
        // Edit mode: calculate what changed and call update
        const currentTestIds = new Set(tests.map((t) => t.id));

        // Tests to remove (were in original but not in current)
        const testsToRemove = [...originalTestIds].filter(
          (id) => !currentTestIds.has(id),
        );

        // New tests (in current but not in original, and don't have websiteId)
        const newTests = tests.filter(
          (t) => !originalTestIds.has(t.id) && !t.websiteId,
        );

        // Website IDs to create (duplicate from existing test websites)
        // For new tests, we need to find a source website to duplicate from
        // We'll use the first existing test's websiteId as the source
        const existingTestWithWebsite = tests.find((t) => t.websiteId);
        const websiteIdsToCreate = newTests.length
          ? newTests.map(() => existingTestWithWebsite?.websiteId || '')
          : undefined;

        await updateExperiment(
          selectedAccount.id,
          dynamicPageId,
          variantId,
          experimentId,
          {
            name,
            metric: selectedMetric,
            secondaryMetrics: selectedSecondaryMetricsData,
            endMode,
            endModeValue: endModeValue ?? null,
            minVisitorsPerVariant:
              endMode === 'statisticalSignificance'
                ? (minVisitorsPerVariant ?? null)
                : null,
            minDurationDays:
              endMode === 'statisticalSignificance'
                ? (minDurationDays ?? null)
                : null,
            tests: tests.map((t) => ({
              id: t.id,
              description: t.description,
              trafficSplit: t.trafficSplit,
              websiteId: t.websiteId,
            })),
            websiteIdsToCreate:
              websiteIdsToCreate && websiteIdsToCreate.length > 0
                ? websiteIdsToCreate.filter(Boolean)
                : undefined,
            websiteIdsToRemove:
              testsToRemove.length > 0
                ? existingExperiment.tests
                    .filter((t) => testsToRemove.includes(t.id))
                    .map((t) => t.websiteId)
                : undefined,
          },
        );
      } else {
        // Create mode
        await startExperiment(selectedAccount.id, dynamicPageId, variantId, {
          name,
          metric: selectedMetric,
          secondaryMetrics: selectedSecondaryMetricsData,
          endMode,
          endModeValue,
          minVisitorsPerVariant:
            endMode === 'statisticalSignificance'
              ? minVisitorsPerVariant
              : undefined,
          minDurationDays:
            endMode === 'statisticalSignificance' ? minDurationDays : undefined,
          tests,
        });
      }

      // Success - close modal and notify parent
      onClose();
      onSuccess?.();
    } catch (err) {
      console.error('Failed to save experiment:', err);
      Sentry.captureException(err);
      setError(
        err instanceof Error ? err.message : 'Failed to save experiment',
      );
    } finally {
      setLoading(false);
    }
  }, [
    canSubmit,
    selectedAccount,
    dynamicPageId,
    variantId,
    availableMetrics,
    name,
    metricId,
    secondaryMetricIds,
    endMode,
    endModeValue,
    minVisitorsPerVariant,
    minDurationDays,
    tests,
    mode,
    experimentId,
    originalTestIds,
    existingExperiment,
    onClose,
    onSuccess,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        onClick: () => onClose(),
      },
      {
        title: mode === 'edit' ? 'Update Experiment' : 'Start Experiment',
        type: 'primary',
        disabled: !canSubmit || loading || fetchingExperiment,
        loading: loading,
        onClick: () => handleSubmit(),
      },
    ],
    [canSubmit, loading, fetchingExperiment, onClose, handleSubmit, mode],
  );

  const selectedEndMode = endModes.find((m) => m.id === endMode);

  // Show loading state when fetching experiment data in edit mode
  if (fetchingExperiment) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        actions={[]}
        width="4xl"
        title="Edit Experiment"
      >
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600" />
            <p className="text-gray-600">Loading experiment data...</p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="4xl"
      title={mode === 'edit' ? 'Edit Experiment' : 'Start Experiment'}
    >
      <div className="flex gap-6">
        {/* Left Column - Settings */}
        <div className="flex flex-1 flex-col gap-4">
          {/* Experiment Name */}
          <Input
            title="Experiment Name*"
            placeholder="Enter a name for this experiment"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          {/* Primary Metric */}
          <div className="flex flex-col gap-0.5">
            <FieldTitle title="Primary Metric*" />
            <p className="mb-0.5 text-sm text-gray-500">
              The main metric you want to optimize for
            </p>
            <Select value={metricId} onValueChange={setMetricId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a metric" />
              </SelectTrigger>
              <SelectContent>
                {availableMetrics.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Secondary Metrics */}
          <div className="flex flex-col gap-0.5">
            <FieldTitle title="Secondary Metrics" />
            <p className="mb-0.5 text-sm text-gray-500">
              Additional metrics to track (optional)
            </p>
            <ChipsAutocomplete<ExperimentMetricConfig>
              placeholder="Search metrics..."
              options={availableSecondaryMetrics}
              selected={selectedSecondaryMetrics}
              onChange={handleSecondaryMetricsChange}
              getValue={(m) => m.name}
              getOptionId={(m) => m.id}
            />
          </div>

          {/* Completion Criteria */}
          <div className="flex flex-col gap-0.5">
            <FieldTitle title="Completion Criteria*" />
            <p className="mb-0.5 text-sm text-gray-500">
              When should the experiment end?
            </p>
            <Select
              value={endMode}
              onValueChange={(v: StartExperimentInput['endMode']) =>
                setEndMode(v)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select completion criteria" />
              </SelectTrigger>
              <SelectContent>
                {endModes.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    <div className="flex flex-col">
                      <span>{m.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedEndMode && (
              <p className="mt-1 text-xs text-gray-400">
                {selectedEndMode.description}
              </p>
            )}

            {/* Statistical Significance options */}
            {endMode === 'statisticalSignificance' && (
              <div className="mt-3 flex gap-3">
                <div className="flex-1">
                  <Input
                    title="Min. Visitors per Variant"
                    type="number"
                    min={1}
                    placeholder="e.g., 100"
                    value={minVisitorsPerVariant?.toString() ?? ''}
                    onChange={(e) =>
                      setMinVisitorsPerVariant(
                        e.target.value
                          ? parseInt(e.target.value, 10)
                          : undefined,
                      )
                    }
                  />
                </div>
                <div className="flex-1">
                  <Input
                    title="Min. Duration (days)"
                    type="number"
                    min={1}
                    placeholder="e.g., 7"
                    value={minDurationDays?.toString() ?? ''}
                    onChange={(e) =>
                      setMinDurationDays(
                        e.target.value
                          ? parseInt(e.target.value, 10)
                          : undefined,
                      )
                    }
                  />
                </div>
              </div>
            )}

            {/* End mode value input */}
            {endMode === 'duration' && (
              <div className="mt-2">
                <Input
                  title="Duration (days)*"
                  type="number"
                  min={1}
                  placeholder="Enter number of days"
                  value={endModeValue?.toString() ?? ''}
                  onChange={(e) =>
                    setEndModeValue(
                      e.target.value ? parseInt(e.target.value, 10) : undefined,
                    )
                  }
                />
              </div>
            )}
            {endMode === 'visitors' && (
              <div className="mt-2">
                <Input
                  title="Target Visitors*"
                  type="number"
                  min={1}
                  placeholder="Enter target visitor count"
                  value={endModeValue?.toString() ?? ''}
                  onChange={(e) =>
                    setEndModeValue(
                      e.target.value ? parseInt(e.target.value, 10) : undefined,
                    )
                  }
                />
              </div>
            )}
          </div>

          {error && (
            <div className="text-end text-sm text-red-600">{error}</div>
          )}
        </div>

        {/* Divider */}
        <div className="w-px bg-gray-200" />

        {/* Right Column - Variations */}
        <div className="flex flex-1 flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <FieldTitle title="Experiment Variations*" />
              <p className="text-sm text-gray-500">
                Add at least one variation to test against the original
              </p>
            </div>
          </div>

          {/* Original (Control) */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-md bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">
                  Original (Control)
                </span>
                <span className="text-sm text-gray-500">Current variant</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Traffic:</span>
                <span
                  className={`font-medium ${
                    originalTrafficSplit < 0 ? 'text-red-600' : 'text-gray-900'
                  }`}
                >
                  {originalTrafficSplit}%
                </span>
              </div>
            </div>
          </div>

          {/* Test Variations */}
          <div className="flex max-h-60 flex-col gap-3 overflow-y-auto">
            {tests.map((test, index) => (
              <div
                key={test.id}
                className="rounded-lg border border-gray-200 bg-white p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md bg-orange-100 px-2 py-1 text-xs font-medium text-orange-700">
                      Variation {String.fromCharCode(65 + index)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      color="negative"
                      onClick={() => removeTest(test.id)}
                      title="Remove variation"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Traffic %</label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      className="min-h-none h-8 w-16 px-2 py-0"
                      value={test.trafficSplit.toString()}
                      onChange={(e) =>
                        updateTestTrafficSplit(
                          test.id,
                          parseInt(e.target.value, 10) || 0,
                        )
                      }
                    />
                  </div>
                </div>
                <TextArea
                  title="What is this variation testing?*"
                  placeholder="e.g., New headline with urgency messaging..."
                  rows={2}
                  value={test.description}
                  onChange={(e) =>
                    updateTestDescription(test.id, e.target.value)
                  }
                />
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            className="w-full"
            size="sm"
            onClick={addTest}
          >
            <Plus className="size-5" /> Add Variation
          </Button>

          {tests.length === 0 && (
            <div className="rounded-lg border-2 border-dashed border-gray-200 p-6 text-center">
              <p className="text-sm text-gray-500">
                No variations added yet. Click &quot;Add Variation&quot; to
                create your first test.
              </p>
            </div>
          )}

          {/* Traffic Distribution Summary */}
          {tests.length > 0 && (
            <div className="flex items-center justify-between rounded-lg bg-gray-100 p-3">
              <span className="text-sm text-gray-600">
                Total Traffic Distribution
              </span>
              <span
                className={`font-medium ${
                  isValidTrafficSplit ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {100 - originalTrafficSplit + originalTrafficSplit}%
                {!isValidTrafficSplit && (
                  <span className="ml-2 text-xs">
                    (Original would receive {originalTrafficSplit}% - invalid)
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
